// ============================================
// Greiði API Client
// ============================================

const API_BASE_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8787'
  : (import.meta.env.VITE_API_URL || 'https://api.greidi.is');

/**
 * API Error class with Icelandic messages
 */
class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Make an API request with automatic error handling
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      const errorMsg = typeof data.error === 'string'
        ? data.error
        : (data.error?.message || JSON.stringify(data.error) || 'Eitthvað fór úrskeiðis');
      throw new ApiError(
        errorMsg,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      'Ekki tókst að tengjast netþjóni',
      0,
      { originalError: error.message }
    );
  }
}

/**
 * Make an authenticated API request (with Auth0 token)
 */
async function authenticatedRequest(endpoint, options = {}, getAccessToken) {
  if (!getAccessToken) {
    throw new Error('getAccessToken function is required for authenticated requests');
  }

  try {
    const token = await getAccessToken();

    return await request(endpoint, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    if (error.status === 401) {
      throw new ApiError('Þú þarft að skrá þig inn', 401, error.data);
    }
    throw error;
  }
}

// ============================================
// JOBS API
// ============================================

export const jobsApi = {
  /**
   * List jobs with filters
   */
  async list({ category, area, status, search, user_id, claimed_by, page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (area) params.append('area', area);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (user_id) params.append('user_id', user_id);
    if (claimed_by) params.append('claimed_by', claimed_by);
    params.append('page', page);
    params.append('limit', limit);

    return await request(`/api/jobs?${params}`);
  },

  /**
   * Full-text search for jobs (public, no auth required)
   * @param {string} query - Search query (min 2 chars)
   * @param {string} [area] - Optional area filter
   * @param {number} [limit=20] - Max results (max 50)
   * @returns {Promise<{success: boolean, data: Array, query: string}>}
   */
  async search(query, area, limit = 20) {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (area && area !== 'Allt') params.set('area', area);
    const res = await fetch(`${API_BASE_URL}/api/jobs/search?${params.toString()}`);
    if (!res.ok) throw new ApiError('Leit mistókst', res.status);
    return res.json();
  },

  /**
   * Get single job by ID
   */
  async get(id) {
    return await request(`/api/jobs/${id}`);
  },

  /**
   * Create a new job (authenticated)
   */
  async create(jobData, getAccessToken) {
    return await authenticatedRequest(
      '/api/jobs',
      {
        method: 'POST',
        body: JSON.stringify(jobData),
      },
      getAccessToken
    );
  },

  /**
   * Update a job (authenticated)
   */
  async update(id, jobData, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(jobData),
      },
      getAccessToken
    );
  },

  /**
   * Delete a job (authenticated)
   */
  async delete(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}`,
      {
        method: 'DELETE',
      },
      getAccessToken
    );
  },

  /**
   * Claim a job (authenticated)
   */
  async claim(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/claim`,
      {
        method: 'POST',
      },
      getAccessToken
    );
  },

  /**
   * Mark job as complete (authenticated)
   */
  async complete(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/complete`,
      {
        method: 'POST',
      },
      getAccessToken
    );
  },

  async release(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/release`,
      {
        method: 'PUT',
      },
      getAccessToken
    );
  },

  async getEvents(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/events`,
      { method: 'GET' },
      getAccessToken
    );
  },

  // ── Add-on viðbætur ──────────────────────────
  async createAddon(jobId, addonData, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${jobId}/addons`,
      { method: 'POST', body: JSON.stringify(addonData) },
      getAccessToken
    );
  },

  async getAddons(jobId, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${jobId}/addons`,
      { method: 'GET' },
      getAccessToken
    );
  },

  async updateAddon(jobId, addonId, status, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${jobId}/addons/${addonId}`,
      { method: 'PUT', body: JSON.stringify({ status }) },
      getAccessToken
    );
  },

  // ── Verkferli (lifecycle) ──────────────────────
  async startJob(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/start`,
      { method: 'PUT' },
      getAccessToken
    );
  },

  async completeJob(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/complete`,
      { method: 'PUT' },
      getAccessToken
    );
  },

  async setPayment(id, paymentData, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/payment`,
      { method: 'PUT', body: JSON.stringify(paymentData) },
      getAccessToken
    );
  },

  async uploadPhoto(id, file, caption, photoType, getAccessToken) {
    const token = await getAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    formData.append('photo_type', photoType || 'progress');
    const res = await fetch(`${API_BASE_URL}/api/jobs/${id}/photos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) throw new ApiError('Upload failed', res.status);
    return await res.json();
  },

  async getPhotos(id, getAccessToken) {
    return await authenticatedRequest(
      `/api/jobs/${id}/photos`,
      { method: 'GET' },
      getAccessToken
    );
  },
};

// ============================================
// COMMENTS API
// ============================================

export const commentsApi = {
  /**
   * Get comments for a job
   */
  async list(jobId) {
    return await request(`/api/comments?job_id=${jobId}`);
  },

  /**
   * Create a comment (authenticated)
   */
  async create(commentData, getAccessToken) {
    return await authenticatedRequest(
      '/api/comments',
      {
        method: 'POST',
        body: JSON.stringify(commentData),
      },
      getAccessToken
    );
  },

  /**
   * Update a comment (admin or owner)
   */
  async update(commentId, text, getAccessToken) {
    return await authenticatedRequest(
      `/api/comments/${commentId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ text }),
      },
      getAccessToken
    );
  },

  /**
   * Delete a comment (admin only)
   */
  async remove(commentId, getAccessToken) {
    return await authenticatedRequest(
      `/api/comments/${commentId}`,
      { method: 'DELETE' },
      getAccessToken
    );
  },
};

// ============================================
// MESSAGES API
// ============================================

export const messagesApi = {
  /**
   * Get messages for a job (authenticated)
   */
  async list(jobId, getAccessToken) {
    return await authenticatedRequest(
      `/api/messages?job_id=${jobId}`,
      {},
      getAccessToken
    );
  },

  /**
   * Send a message (authenticated)
   */
  async create(messageData, getAccessToken) {
    return await authenticatedRequest(
      '/api/messages',
      {
        method: 'POST',
        body: JSON.stringify(messageData),
      },
      getAccessToken
    );
  },

  /**
   * Mark message as read (authenticated)
   */
  async markRead(messageId, getAccessToken) {
    return await authenticatedRequest(
      `/api/messages/${messageId}/read`,
      {
        method: 'PUT',
      },
      getAccessToken
    );
  },

  /**
   * Get unread message count (authenticated)
   */
  async getUnreadCount(getAccessToken) {
    return await authenticatedRequest(
      '/api/messages/unread-count',
      {},
      getAccessToken
    );
  },
};

// ============================================
// OFFERS API
// ============================================

export const offersApi = {
  /**
   * Get offers for a job (authenticated)
   */
  async list(jobId, getAccessToken) {
    return await authenticatedRequest(
      `/api/offers?job_id=${jobId}`,
      {},
      getAccessToken
    );
  },

  /**
   * Create an offer (authenticated)
   */
  async create(offerData, getAccessToken) {
    return await authenticatedRequest(
      '/api/offers',
      {
        method: 'POST',
        body: JSON.stringify(offerData),
      },
      getAccessToken
    );
  },

  /**
   * Accept or reject an offer (authenticated)
   */
  async updateStatus(offerId, status, getAccessToken) {
    return await authenticatedRequest(
      `/api/offers/${offerId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ status }),
      },
      getAccessToken
    );
  },

  /**
   * Get accepted offer for a job (for chat pickup display)
   */
  async getAccepted(jobId, getAccessToken) {
    return await authenticatedRequest(
      `/api/offers/accepted?job_id=${jobId}`,
      {},
      getAccessToken
    );
  },
};

// ============================================
// REVIEWS API
// ============================================

export const reviewsApi = {
  /**
   * Get reviews for a user
   */
  async list(userId) {
    return await request(`/api/reviews?user_id=${userId}`);
  },

  /**
   * Create a review (authenticated)
   */
  async create(reviewData, getAccessToken) {
    return await authenticatedRequest(
      '/api/reviews',
      {
        method: 'POST',
        body: JSON.stringify(reviewData),
      },
      getAccessToken
    );
  },

  /**
   * Check if user has reviewed a job (authenticated)
   */
  async checkReviewed(jobId, getAccessToken) {
    return await authenticatedRequest(
      `/api/reviews/check?job_id=${jobId}`,
      {},
      getAccessToken
    );
  },
};

// ============================================
// IMAGES API
// ============================================

export const imagesApi = {
  /**
   * Upload an image to R2 (authenticated)
   * @param {File} file - The image file to upload
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   * @returns {Promise<{success: boolean, data: {key: string, url: string, contentType: string, size: number}}>}
   */
  async upload(file, getAccessToken) {
    if (!getAccessToken) {
      throw new Error('getAccessToken function is required for authenticated requests');
    }

    const token = await getAccessToken();

    const formData = new FormData();
    formData.append('image', file);

    const url = `${API_BASE_URL}/api/images/upload`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Do NOT set Content-Type - browser sets it with boundary for multipart
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.error || 'Ekki tókst að hlaða upp mynd',
        response.status,
        data
      );
    }

    return data;
  },

  /**
   * Upload multiple images to R2 in one request (authenticated)
   * @param {File[]} files - Array of image files to upload (max 6)
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   * @returns {Promise<{success: boolean, data: Array<{key: string, url: string}>}>}
   */
  async uploadBatch(files, getAccessToken) {
    if (!getAccessToken) {
      throw new Error('getAccessToken function is required for authenticated requests');
    }

    const token = await getAccessToken();
    const formData = new FormData();
    files.forEach((file, i) => formData.append(`image_${i}`, file));

    const url = `${API_BASE_URL}/api/images/upload/batch`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(data.error || 'Ekki tókst að hlaða upp myndum', response.status, data);
    }
    return data;
  },

  /**
   * Get the full URL for an image key
   * @param {string} keyOrUrl - Image key or relative URL
   * @returns {string} Full image URL
   */
  getUrl(keyOrUrl) {
    if (!keyOrUrl) return null;
    // If it's already a full URL, return it
    if (keyOrUrl.startsWith('http')) return keyOrUrl;
    // Static assets served directly (SVG icons, etc.)
    if (keyOrUrl.startsWith('/icons/')) return keyOrUrl;
    // If it's a relative API path, prepend base URL
    if (keyOrUrl.startsWith('/api/')) return `${API_BASE_URL}${keyOrUrl}`;
    // Otherwise assume it's a key
    return `${API_BASE_URL}/api/images/${keyOrUrl}`;
  },

  /**
   * Delete an image from R2 (authenticated)
   * @param {string} key - The image key
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async delete(key, getAccessToken) {
    return await authenticatedRequest(
      `/api/images/${key}`,
      {
        method: 'DELETE',
      },
      getAccessToken
    );
  },
};

// ============================================
// AUTH API
// ============================================

export const authApi = {
  /**
   * Sync Auth0 user to database (authenticated)
   */
  async syncUser(userData, getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/sync',
      {
        method: 'POST',
        body: JSON.stringify(userData),
      },
      getAccessToken
    );
  },

  /**
   * Get current user (authenticated)
   */
  async getCurrentUser(getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/me',
      {},
      getAccessToken
    );
  },

  /**
   * Link a secondary identity (e.g., Google) to the current user (authenticated)
   * @param {string} idToken - ID token from secondary provider login (Auth0 popup)
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async linkAccount(idToken, getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/link-account',
      {
        method: 'POST',
        body: JSON.stringify({ idToken }),
      },
      getAccessToken
    );
  },

  /**
   * Get linked identities for current user (authenticated)
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   * @returns {Promise<{success: boolean, data: {identities: Array}}>}
   */
  async getIdentities(getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/identities',
      {},
      getAccessToken
    );
  },

  async sendPhoneOtp(phone, getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/phone/send-otp',
      { method: 'POST', body: JSON.stringify({ phone }) },
      getAccessToken
    );
  },

  async verifyPhoneOtp(code, getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/phone/verify',
      { method: 'POST', body: JSON.stringify({ code }) },
      getAccessToken
    );
  },

  async updateProfile(data, getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/me',
      { method: 'PUT', body: JSON.stringify(data) },
      getAccessToken
    );
  },

  /**
   * Deactivate current user's account (soft-delete, authenticated)
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async deactivateAccount(getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/me',
      { method: 'DELETE' },
      getAccessToken
    );
  },

  /**
   * Get or generate referral code for current user (authenticated)
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async getReferral(getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/referral',
      {},
      getAccessToken
    );
  },

  /**
   * Apply a referral code during onboarding (authenticated)
   * @param {string} code - Referral code to apply
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async applyReferral(code, getAccessToken) {
    return await authenticatedRequest(
      '/api/auth/referral/apply',
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
      getAccessToken
    );
  },
};

// ============================================
// AI API
// ============================================

export const aiApi = {
  /**
   * Analyze an image with AI (authenticated)
   * @param {string} imageKey - The R2 image key
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async analyzeImage(imageKey, getAccessToken) {
    return await authenticatedRequest(
      '/api/ai/analyze-image',
      {
        method: 'POST',
        body: JSON.stringify({ image_key: imageKey }),
      },
      getAccessToken
    );
  },

  /**
   * Estimate price for a job (authenticated)
   * @param {string} category - Job category
   * @param {string} area - Geographic area
   * @param {string} description - Job description
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async estimatePrice(category, area, description, getAccessToken) {
    return await authenticatedRequest(
      '/api/ai/estimate-price',
      {
        method: 'POST',
        body: JSON.stringify({ category, area, description }),
      },
      getAccessToken
    );
  },

  /**
   * Chat with Ragga AI assistant (no auth required)
   * @param {string} message - User message
   * @param {Array} history - Conversation history [{role, content}]
   */
  async chat(message, history = []) {
    return await request('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ message, history }),
    });
  },

  /**
   * Classify a job from a photo using Gemini Vision (authenticated)
   * @param {string} imageBase64 - Base64 encoded image
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   * @returns {Promise<{success: boolean, data: {category, job_type, title_suggestion, description_suggestion, price_low, price_high, price_tier, confidence, disposal_bin?, disposal_info?}}>}
   */
  async classifyJob(imageBase64, getAccessToken) {
    return await authenticatedRequest(
      '/api/ai/classify-job',
      {
        method: 'POST',
        body: JSON.stringify({ image: imageBase64 }),
      },
      getAccessToken
    );
  },

  /**
   * Generate a cute cartoon icon from a photo (authenticated)
   * @param {string} imageBase64 - Base64 encoded image
   * @param {string} itemName - Item name for better icon generation
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   * @returns {Promise<{success: boolean, data: {icon_key, icon_url, mime_type}}>}
   */
  async generateIcon(imageBase64, itemName, getAccessToken) {
    return await authenticatedRequest(
      '/api/ai/generate-icon',
      {
        method: 'POST',
        body: JSON.stringify({ image: imageBase64, item_name: itemName }),
      },
      getAccessToken
    );
  },

  /**
   * Generate an icon from text description (no photo needed)
   * @param {string} description - Text description of the item/task
   * @param {string} category - Job category for better icon generation
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   * @returns {Promise<{success: boolean, data: {icon_key, icon_url, mime_type}}>}
   */
  async generateIconFromText(description, category, getAccessToken) {
    return await authenticatedRequest(
      '/api/ai/generate-icon-from-text',
      {
        method: 'POST',
        body: JSON.stringify({ description, category }),
      },
      getAccessToken
    );
  },

  /**
   * Get disposal info for Senda & Henda (no auth required)
   * @param {string} bin - Bin type (paper, plastic, food, mixed, recycling_center, deposit)
   * @param {string} size - Item size (small, medium, large)
   * @returns {Promise<{success: boolean, data: {price_low, price_high, destination, bin_info, nearest_station}}>}
   */
  /**
   * Generate Ragga AI comment for a job (authenticated)
   * @param {string} jobId - Job ID
   * @param {object} jobData - { title, description, category, job_type }
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async getJobComment(jobId, jobData, getAccessToken, { existingComments, force } = {}) {
    return await authenticatedRequest(
      '/api/ai/job-comment',
      {
        method: 'POST',
        body: JSON.stringify({
          job_id: jobId,
          ...jobData,
          ...(existingComments?.length ? { existing_comments: existingComments } : {}),
          ...(force ? { force: true } : {}),
        }),
      },
      getAccessToken
    );
  },

  /**
   * Estimate size/weight and suggest pricing for an add-on item (no auth required)
   * @param {string} description - Item description
   * @param {string|null} imageUrl - Optional image URL
   * @returns {Promise<{success: boolean, data: {weight_class, estimated_kg, needs_extra_person, suggested_price_isk, reasoning}}>}
   */
  async estimateSize(description, imageUrl = null) {
    return await request('/api/ai/estimate-size', {
      method: 'POST',
      body: JSON.stringify({ description, imageUrl }),
    });
  },

  async getDisposalInfo(bin, size = 'medium') {
    const params = new URLSearchParams();
    if (bin) params.append('bin', bin);
    if (size) params.append('size', size);
    return await request(`/api/ai/disposal-info?${params}`);
  },
};

// ============================================
// CALENDAR API
// ============================================

export const calendarApi = {
  /**
   * Get jobs grouped by date for a time range
   * @param {number} startDate - Unix seconds
   * @param {number} endDate - Unix seconds
   * @param {string} area - Optional area filter
   */
  async getJobsByDateRange(startDate, endDate, area) {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    if (area) params.append('area', area);
    return await request(`/api/jobs/calendar?${params}`);
  },
};

// ============================================
// DRIVERS API
// ============================================

export const driversApi = {
  /**
   * Get current driver information
   */
  async getMe(getAccessToken) {
    return await authenticatedRequest('/api/drivers/me', { method: 'GET' }, getAccessToken);
  },

  /**
   * Get driver's active jobs
   */
  async getActiveJobs(getAccessToken) {
    return await authenticatedRequest('/api/drivers/active-jobs', { method: 'GET' }, getAccessToken);
  },

  /**
   * Check if a job fits in a vehicle
   * @param {string} jobId - Job ID
   * @param {string} vehicleType - Vehicle type (folksbill, sendibill_litill, etc.)
   */
  async checkCapacity(jobId, vehicleType, getAccessToken) {
    return await authenticatedRequest('/api/drivers/check-capacity', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId, vehicle_type: vehicleType }),
    }, getAccessToken);
  },

  /**
   * Register as a driver
   * @param {object} driverData - { vehicle_type, license_plate?, capacity_kg? }
   */
  async register(driverData, getAccessToken) {
    return await authenticatedRequest('/api/drivers/register', {
      method: 'POST',
      body: JSON.stringify(driverData),
    }, getAccessToken);
  },

  /**
   * Start timer for a job
   * @param {string} jobId - Job ID
   */
  async startTimer(jobId, getAccessToken) {
    return await authenticatedRequest(`/api/jobs/${jobId}/start-timer`, {
      method: 'POST',
    }, getAccessToken);
  },

  /**
   * Stop timer and calculate overtime
   * @param {string} jobId - Job ID
   */
  async stopTimer(jobId, getAccessToken) {
    return await authenticatedRequest(`/api/jobs/${jobId}/stop-timer`, {
      method: 'POST',
    }, getAccessToken);
  },
};

// ============================================
// ROUTES API (Multi-Stop Batching)
// ============================================

export const routesApi = {
  /**
   * Create a new route with multiple jobs
   * @param {string[]} jobIds - Array of job IDs
   * @param {boolean} optimize - Whether to optimize route order (default: true)
   */
  async create(jobIds, optimize = true, getAccessToken) {
    return await authenticatedRequest('/api/routes/create', {
      method: 'POST',
      body: JSON.stringify({ job_ids: jobIds, optimize }),
    }, getAccessToken);
  },

  /**
   * Get driver's routes
   * @param {string} status - Filter by status: 'active' | 'completed' | 'all' (default: 'active')
   */
  async getMyRoutes(status = 'active', getAccessToken) {
    return await authenticatedRequest(`/api/routes/my-routes?status=${status}`, {
      method: 'GET',
    }, getAccessToken);
  },

  /**
   * Reorder stops in a route
   * @param {string} routeId - Route ID
   * @param {string[]} jobIds - New order of job IDs
   */
  async reorder(routeId, jobIds, getAccessToken) {
    return await authenticatedRequest(`/api/routes/${routeId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ job_ids: jobIds }),
    }, getAccessToken);
  },

  /**
   * Mark route as completed
   * @param {string} routeId - Route ID
   */
  async complete(routeId, getAccessToken) {
    return await authenticatedRequest(`/api/routes/${routeId}/complete`, {
      method: 'POST',
    }, getAccessToken);
  },

  /**
   * Optimize a list of jobs using AI
   * @param {string[]} jobIds - Array of job IDs to optimize
   * @returns {Promise<{optimized_order: string[], total_distance_km: number, total_duration_min: number}>}
   */
  async optimize(jobIds, getAccessToken) {
    return await authenticatedRequest('/api/routes/optimize', {
      method: 'POST',
      body: JSON.stringify({ job_ids: jobIds }),
    }, getAccessToken);
  },
};

// ============================================
// ADMIN API
// ============================================

export const adminApi = {
  /**
   * Get admin dashboard statistics
   */
  async getStats(getAccessToken) {
    return await authenticatedRequest('/api/admin/stats', {
      method: 'GET',
    }, getAccessToken);
  },

  /**
   * List jobs for admin view
   * @param {object} opts - { status?, limit? }
   */
  async listJobs({ status, limit = 20 } = {}, getAccessToken) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    return await authenticatedRequest(`/api/admin/jobs?${params}`, {
      method: 'GET',
    }, getAccessToken);
  },

  /**
   * List users for admin view
   * @param {object} opts - { sort?, limit? }
   */
  async listUsers({ sort = 'recent', limit = 20 } = {}, getAccessToken) {
    const params = new URLSearchParams();
    params.append('sort', sort);
    params.append('limit', limit.toString());
    return await authenticatedRequest(`/api/admin/users?${params}`, {
      method: 'GET',
    }, getAccessToken);
  },

  /**
   * Disable (hide) a job — sets status to 'disabled'
   * @param {string} jobId - Job ID to disable
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async disableJob(jobId, getAccessToken) {
    return await authenticatedRequest(`/api/admin/jobs/${jobId}/disable`, {
      method: 'PUT',
    }, getAccessToken);
  },

  /**
   * Hard delete a job and all related data (comments, offers, messages, reviews)
   * @param {string} jobId - Job ID to delete
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async deleteJob(jobId, getAccessToken) {
    return await authenticatedRequest(`/api/admin/jobs/${jobId}`, {
      method: 'DELETE',
    }, getAccessToken);
  },

  /**
   * Set a user's role (admin or user)
   * @param {string} userId - User ID
   * @param {'user'|'admin'} role - New role
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async setUserRole(userId, role, getAccessToken) {
    return await authenticatedRequest(`/api/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }, getAccessToken);
  },
};

// ============================================
// PUSH NOTIFICATIONS API
// ============================================

export const pushApi = {
  /**
   * Subscribe to push notifications (authenticated)
   * @param {PushSubscription} subscription - Browser push subscription object
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async subscribe(subscription, getAccessToken) {
    const subJson = subscription.toJSON();
    return await authenticatedRequest(
      '/api/push/subscribe',
      {
        method: 'POST',
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
        }),
      },
      getAccessToken
    );
  },

  /**
   * Unsubscribe from push notifications (authenticated)
   * @param {string} [endpoint] - Specific endpoint to unsubscribe, or all if omitted
   * @param {Function} getAccessToken - Auth0 getAccessTokenSilently
   */
  async unsubscribe(endpoint, getAccessToken) {
    return await authenticatedRequest(
      '/api/push/unsubscribe',
      {
        method: 'POST',
        body: JSON.stringify(endpoint ? { endpoint } : {}),
      },
      getAccessToken
    );
  },
};

// Export API error for error handling
export { ApiError };
